import React, { useEffect, useState, useMemo } from 'react';
import { Tabs, theme } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  PointerSensor,
  useSensor,
  DragEndEvent
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/css';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { GapTabs, DraggableTabPaneProps } from 'store/ducks/Gaps/types';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import i18n from 'constants/translation/i18n';
import SaveModal from 'components/SaveModal';
import Helpers from 'utils/Helpers';
import ComplianceEstLayout from 'components/Layouts/ComplianceEstLayout';
import GapsDescriptionTable from 'components/Tables/GapsDescriptionTable';
import AdditionalInfoTable from 'components/Tables/AdditionalInfoTable';
import * as Styled from './GapsAndAuditFindings.styled';

const { useToken } = theme;

const DraggableTabNode = ({
  className,
  onActiveBarTransform,
  ...props
}: DraggableTabPaneProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isSorting
  } = useSortable({
    id: props['data-node-key']
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move'
  };

  useEffect(() => {
    if (!isSorting) {
      onActiveBarTransform('');
    } else if (className?.includes('ant-tabs-tab-active')) {
      onActiveBarTransform(
        css`
          .ant-tabs-ink-bar {
            transform: ${CSS.Transform.toString(transform)};
            transition: ${transition} !important;
          }
        `
      );
    }
  }, [className, isSorting, transform]);

  return React.cloneElement(props.children as React.ReactElement, {
    ref: setNodeRef,
    style,
    ...attributes,
    ...listeners
  });
};

const GapsTable: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = useToken();

  const { infoSave, gapsSaveData } = useSelector(
    (state: RootState) => state.gaps
  );
  const { lang, scheme } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    scheme: state.global.scheme
  }));

  const [activeTab, setActiveTab] = useState<GapTabs | string>(
    GapTabs.GAPS_DESC
  );
  const [activeKey, setActiveKey] = useState<string>('3');
  const [className, setClassName] = useState('');
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [keyTobeSwitched, setKeyTobeSwitched] = useState<GapTabs | string>('');
  const [items, setItems] = useState([
    {
      key: '3',
      label: i18n.t('GAPS_DESCRIPTION'),
      children: <GapsDescriptionTable />
    },
    {
      key: '4',
      label: i18n.t('ADDITIONAL_DESCRIPTION'),
      children: <AdditionalInfoTable />
    }
  ]);

  const hasChanges = useMemo(() => {
    const hasGapsChanges = Object.keys(gapsSaveData).length > 0;
    const hasInfoChanges = Object.keys(infoSave).length > 0;
    return hasGapsChanges || hasInfoChanges;
  }, [gapsSaveData, infoSave]);

  useUnsavedChanges({ hasChanges });

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 }
  });

  const handleTabClick = (key: string, tabValue: GapTabs) => {
    // Clear assigned data automatically for audit findings and evidences
    // if (activeTab === GapTabs.AUDIT_FINDINGS && assignedEls.length) {
    //   dispatch(gapsOp.setAssignedEls([]));
    //   dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
    // }
    // if (activeTab === GapTabs.EVIDENCES && assignedEvidences.length) {
    //   dispatch(gapsOp.setAssignedEvidences([]));
    //   dispatch(gapsOp.setAssignedEvidencesUpdated(true));
    // }

    // Only show modal for gaps description and additional info
    if (
      (activeTab === GapTabs.GAPS_DESC && !Helpers.isObjEmpty(gapsSaveData)) ||
      (activeTab === GapTabs.ADDITIONAL_INFO && !Helpers.isObjEmpty(infoSave))
    ) {
      setKeyTobeSwitched(key);
      setModalOpen(true);
      return;
    } else {
      setActiveTab(tabValue);
      setActiveKey(key);
      dispatch(complianceEstOp.setDrawTable(false));
    }
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalOk = () => {
    dispatch(complianceEstOp.setSaveClicked(true));
    setModalOpen(false);

    if (keyTobeSwitched) {
      setActiveKey(keyTobeSwitched);

      const foundObject = items.find((obj) => obj.key === keyTobeSwitched);

      if (foundObject) {
        setActiveTab(foundObject.label);
      }

      setKeyTobeSwitched('');
    }

    // Clear unsaved changes after save
    // Note: The actual clearing happens in the Redux actions
  };

  const handleModalCancel = () => {
    setModalOpen(false);
  };

  const handleDiscardChanges = () => {
    handleModalCancel();

    switch (activeTab) {
      // case GapTabs.EVIDENCES:
      //   if (assignedEvidences.length) {
      //     dispatch(gapsOp.setAssignedEvidences([]));
      //     dispatch(gapsOp.setAssignedEvidencesUpdated(true));
      //   }
      //   break;
      case GapTabs.GAPS_DESC:
        if (!Helpers.isObjEmpty(gapsSaveData)) {
          dispatch(gapsOp.setGapsSaveData({}));
          dispatch(gapsOp.setGapsUpdated(true));
        }
        break;
      case GapTabs.ADDITIONAL_INFO:
        if (!Helpers.isObjEmpty(infoSave)) {
          dispatch(gapsOp.setInfoSaveData({}));
          dispatch(gapsOp.setInfoUpdated(true));
        }
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    const updatedItems = items.map((item) => {
      switch (item.key) {
        // case '2':
        //   return { ...item, label: i18n.t('EVIDENCE_MAPPING') };
        case '3':
          return { ...item, label: i18n.t('GAPS_DESCRIPTION') };
        case '4':
          return {
            ...item,
            label: i18n.t('ADDITIONAL_DESCRIPTION')
          };
        default:
          return item;
      }
    });
    setItems(updatedItems);
  }, [lang]);

  return (
    <ComplianceEstLayout activeTab={activeTab} activeKey={activeKey}>
      <Styled.ContentWrapper token={token} scheme={scheme}>
        <Tabs
          className={className}
          defaultActiveKey='3'
          activeKey={activeKey}
          onTabClick={(key: string, event: any) =>
            handleTabClick(key, event.target.textContent)
          }
          items={items}
          renderTabBar={(tabBarProps, DefaultTabBar) => (
            <DndContext
              sensors={[sensor]}
              onDragEnd={({ active, over }: DragEndEvent) =>
                Helpers.onDragEnd(
                  {
                    active,
                    over
                  },
                  setItems,
                  'key'
                )
              }
            >
              <SortableContext
                items={items.map((i) => i.key)}
                strategy={horizontalListSortingStrategy}
              >
                <DefaultTabBar {...tabBarProps}>
                  {(node) => (
                    <DraggableTabNode
                      {...node.props}
                      key={node.key}
                      onActiveBarTransform={setClassName}
                    >
                      {node}
                    </DraggableTabNode>
                  )}
                </DefaultTabBar>
              </SortableContext>
            </DndContext>
          )}
        />
        <SaveModal
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          handleOk={handleModalOk}
          handleCancel={handleModalCancel}
          handleOpen={handleModalOpen}
          handleDiscard={handleDiscardChanges}
        />
      </Styled.ContentWrapper>
    </ComplianceEstLayout>
  );
};

export default GapsTable;
